import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  closestCenter,
  CollisionDetection,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  getFirstCollision,
  pointerWithin,
  rectIntersection,
  UniqueIdentifier,
} from "@dnd-kit/core";
import { debounce, isEqual } from "lodash-es";

import { reorderMultipleDNDLists } from "utils/dnd";
import usePrevious from "hooks/usePrevious";

import { Column, ColumnId } from "./types";
import { sliceHiddenItems } from "./utils";

export const CONTAINERS = [ColumnId.First, ColumnId.Second];

type UseTwoColumnDND<T extends string, U extends Record<string, unknown> | undefined> = {
  onChange: (props: { first: Column<T, U>; second: Column<T, U> }) => void;
  firstColumn: Column<T, U>;
  secondColumn: Column<T, U>;
  minItems?: Record<ColumnId, number>;
  disabledItemsInsideContainer?: T[];
};

const useTwoColumnDND = <T extends string, U extends Record<string, unknown> | undefined>({
  onChange,
  firstColumn,
  secondColumn = [],
  minItems,
  disabledItemsInsideContainer,
}: UseTwoColumnDND<T, U>) => {
  const [activeElementId, setActiveElementId] = useState<null | T>(null);
  const [activeContainerId, setActiveContainerId] = useState<null | ColumnId>(null);
  const { visible: firstVisible, hidden: firstHidden } = sliceHiddenItems(firstColumn);
  const { visible: secondVisible, hidden: secondHidden } = sliceHiddenItems(secondColumn);

  // Create initial container state from visible items
  const initialContainersState: Record<string, T[]> = useMemo(
    () => ({
      first: firstVisible.map((v) => v.value),
      second: secondVisible.map((v) => v.value),
    }),
    [firstVisible, secondVisible]
  );

  const [containersState, setContainersState] = useState(initialContainersState);

  const previousInitialContainersState = usePrevious(initialContainersState);
  const recentlyMovedToNewContainer = useRef(false);
  const lastOverId = useRef<UniqueIdentifier | null>(null);

  // Reset container state when initial data changes
  useEffect(() => {
    const shouldReset =
      !isEqual(previousInitialContainersState, initialContainersState) &&
      !isEqual(containersState, initialContainersState) &&
      !activeElementId;

    if (shouldReset) {
      setContainersState(initialContainersState);
    }
  }, [initialContainersState, containersState, activeElementId, previousInitialContainersState]);

  // Utility to find which container holds an item
  const findContainer = useCallback(
    (id: T) =>
      Object.keys(containersState).find((key) => containersState[key].includes(id)) as
        | ColumnId
        | undefined,
    [containersState]
  );

  const allVisibleItems = useMemo(
    () => [...firstVisible, ...secondVisible].map((v) => v.value),
    [firstVisible, secondVisible]
  );

  const handleDragStart = useCallback(
    ({ active }: DragStartEvent) => {
      setActiveElementId(active.id as T);
      setActiveContainerId(findContainer(active.id as T) as ColumnId);
    },
    [findContainer]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveElementId(null);
      setActiveContainerId(null);

      if (!event.over) return;

      const oldItems = {
        first: [...firstVisible, ...firstHidden],
        second: [...secondVisible, ...secondHidden],
      };

      const old = {
        first: firstVisible.map(({ value }) => value),
        second: secondVisible.map(({ value }) => value),
      };

      const sourceContainer = Object.keys(old).find((key) =>
        old[key as ColumnId].includes(event.active.id as T)
      );
      const targetContainer = findContainer(event.active.id as T);

      if (!sourceContainer || !targetContainer) return;

      const sourceIndex = old[sourceContainer as ColumnId].indexOf(event.active.id as T);
      const targetIndex = containersState[targetContainer].indexOf(event.over.id as T);

      const { first, second } = reorderMultipleDNDLists(
        oldItems,
        sourceContainer,
        targetContainer,
        sourceIndex,
        targetIndex
      );

      // Update state with non-hidden items
      const filterVisible = (column: Column<T, U>) =>
        column.reduce((acc, next) => (next.hidden ? acc : [...acc, next.value]), [] as T[]);

      setContainersState({
        first: filterVisible(first),
        second: filterVisible(second),
      });

      onChange({ first, second });
    },
    [
      firstVisible,
      secondVisible,
      onChange,
      containersState,
      findContainer,
      firstHidden,
      secondHidden,
    ]
  );

  // Reset to initial state on drag cancel
  const handleDragCancel = useCallback(() => {
    setContainersState(initialContainersState);
    setActiveElementId(null);
    setActiveContainerId(null);
  }, [initialContainersState]);

  const collisionDetection: CollisionDetection = useCallback(
    (args) => {
      // Handle container sorting
      if (activeElementId && activeElementId in containersState) {
        return closestCenter(args);
      }

      let overId = getFirstCollision(
        pointerWithin(args).length > 0 ? pointerWithin(args) : rectIntersection(args),
        "id"
      );
      if (overId && !recentlyMovedToNewContainer.current) {
        // If overId is container, find the closest item it
        if (overId in containersState && containersState[overId as T].length > 0) {
          overId = closestCenter({
            ...args,
            droppableContainers: args.droppableContainers.filter((container) =>
              containersState[overId as T].includes(container.id as T)
            ),
          })[0]?.id;
        }

        lastOverId.current = overId;
        return [{ id: overId }];
      }

      if (recentlyMovedToNewContainer.current) {
        lastOverId.current = activeElementId;
      }

      return lastOverId.current ? [{ id: lastOverId.current }] : [];
    },
    [activeElementId, containersState]
  );

  const handleDragMove = useCallback(() => {
    recentlyMovedToNewContainer.current = false;
  }, []);

  const handleDragOver = useCallback(
    ({ active, over, activatorEvent }: DragOverEvent) => {
      const overId = over?.id;

      // Exit early if no valid target or if dragging an item container
      if (!overId || active.id in allVisibleItems) return;

      // Determine containers
      const overContainer = CONTAINERS.includes(overId as ColumnId)
        ? overId
        : findContainer(overId as T);
      const activeContainer = findContainer(active.id as T);

      // Exit if either container is not found
      if (!overContainer || !activeContainer) return;

      // Only process when moving between containers
      if (activeContainer !== overContainer) {
        setContainersState((containers) => {
          const activeItems = containers[activeContainer];
          const overItems = containers[overContainer];

          const disabledByContainersLimits =
            activeItems.length <= (minItems?.[activeContainer as ColumnId] || 0);
          const disabledMoveToOtherContainer =
            disabledItemsInsideContainer &&
            activeItems.find(
              (item) => disabledItemsInsideContainer.includes(item) && activeElementId === item
            );

          if (disabledByContainersLimits || disabledMoveToOtherContainer) return containers;

          const activeItem = active.id as T;

          // Determine insertion position
          let newIndex = overItems.length;

          if (!(overId in allVisibleItems) && over) {
            const overIndex = overItems.indexOf(overId as T);

            if (overIndex >= 0) {
              const shouldBePlacedBelowOverItem =
                active.rect.current.translated &&
                active.rect.current.translated.top > over.rect.top + over.rect.height;

              newIndex = shouldBePlacedBelowOverItem ? overIndex + 1 : overIndex;
            }
          }

          // This condition solves issue with moving items between containers when using keyboard
          if (activatorEvent.type !== "keydown") {
            recentlyMovedToNewContainer.current = true;
          }

          // Create new state with item moved between containers
          return {
            [activeContainer]: activeItems.filter((item) => item !== activeItem),
            [overContainer]: [
              ...overItems.slice(0, newIndex),
              activeItem,
              ...overItems.slice(newIndex),
            ],
          };
        });
      }
    },
    [findContainer, allVisibleItems, minItems, disabledItemsInsideContainer, activeElementId]
  );

  // It improves performance of dragging items between containers and solves fast jumping content for edge case layout shifting when moving from one container to another
  const debouncedHandleDragOver = useMemo(() => debounce(handleDragOver, 30), [handleDragOver]);

  const isFirstColumnDisabled = useMemo(() => {
    if (activeContainerId !== ColumnId.Second) {
      return false;
    }

    if (
      containersState.second.find(
        (item) => disabledItemsInsideContainer?.includes(item) && activeElementId === item
      )
    ) {
      return true;
    }

    return (
      !!minItems?.[ColumnId.Second] &&
      initialContainersState.second.length <= minItems[ColumnId.Second]
    );
  }, [
    minItems,
    containersState.second,
    activeContainerId,
    disabledItemsInsideContainer,
    activeElementId,
    initialContainersState.second.length,
  ]);

  const isSecondColumnDisabled = useMemo(() => {
    if (activeContainerId !== ColumnId.First) {
      return false;
    }

    if (
      containersState.first.find(
        (item) => disabledItemsInsideContainer?.includes(item) && activeElementId === item
      )
    ) {
      return true;
    }

    return (
      !!minItems?.[ColumnId.First] &&
      initialContainersState.first.length <= minItems?.[ColumnId.First]
    );
  }, [
    minItems,
    containersState.first,
    activeContainerId,
    disabledItemsInsideContainer,
    activeElementId,
    initialContainersState.first.length,
  ]);

  return {
    firstContainerItems: containersState.first,
    secondContainerItems: containersState.second,
    isFirstColumnDisabled,
    isSecondColumnDisabled,
    handleDragStart,
    handleDragOver: debouncedHandleDragOver,
    handleDragCancel,
    handleDragEnd,
    handleDragMove,
    collisionDetection,
    isDragActive: activeElementId != null,
    activeContainerId,
    activeElementId,
    isEmpty: !containersState.first.length && !containersState.second.length,
  };
};

export default useTwoColumnDND;
